import { Div, Icon, Label, useWindowSize } from "@grassrootly/rn-core";
import { ScreenLayout } from "../../layout/components";
import { MainStackScreenProps } from "../../navigation/Navigation.types";
import { useCompetitionDetail } from "./useCompetitionDetail";
import { Image } from "react-native";
import { images } from "../../layout/assets";
import CompetitionTab from "./CompetitionTab";
import CompetitionStandingsTab from "./CompetitionStandingsTab";
import CompetitionTopPlayersTab from "./CompetitionTopPlayersTab";
import CompetitionPlayerStatsTab from "./CompetitionPlayerStatsTab";
import CompetitionMatchesTab from "./CompetitionMatchesTab";
import SeasonSelector from "./SeasonSelector";

export default function CompetitionDetailScreen(props: MainStackScreenProps<"competition-detail">) {
  const {
    competitionId,
    competitionName,
    competition,
    seasons,
    selectedSeasonId,
    selectedTab,
    tabs,
    onTabSelected,
    onSeasonSelected,
    handleNavigationPress,
  } = useCompetitionDetail(props);

  const { width } = useWindowSize();

  return (
    <ScreenLayout
      header={{
        center: null,
        left: (
          <Icon
            classNames={["mb-{auto}", "mt-{8}"]}
            name="arrow_back_ios"
            size={24}
            onPress={handleNavigationPress}
          />
        ),
      }}
      headerPaddingWeb={false}
    >
      {/* Competition Title Section */}
      <Div classNames={["mt-{70}", "mb-{24}", "flexRow", "alignItemsCenter", "justifyContentSpaceBetween"]}>
        <Div classNames={["flexRow", "alignItemsCenter", "flex1", "mr-{16}"]}>
          <Image
            source={
              competition?.organization_logo
                ? { uri: competition.organization_logo }
                : images.groupAvatar
            }
            style={{ width: 56, height: 56, borderRadius: 12, marginRight: 16 }}
            resizeMode="cover"
          />
          <Div classNames={["flex1"]}>
            <Label type="l1Bold" numberOfLines={2} classNames={["fontSize-{24}"]}>
              {competitionName}
            </Label>
            {competition?.organization_name && (
              <Label type="l3Regular" classNames={["colorSecondaryLight", "mt-{4}"]}>
                {competition.organization_name}
              </Label>
            )}
          </Div>
        </Div>
        
        {/* Season Selector - Compact */}
        {seasons.length > 0 && (
          <SeasonSelector
            seasons={seasons}
            selectedSeasonId={selectedSeasonId}
            onSeasonSelected={onSeasonSelected}
            compact
          />
        )}
      </Div>

      {/* Tabs */}
      <Div classNames={["mb-{20}"]}>
        <Div classNames={["flexRow", "gap8"]}>
          {tabs.map((tab) => (
            <CompetitionTab
              key={tab.alias}
              alias={tab.alias}
              isActive={selectedTab === tab.alias}
              title={tab.title}
              onPress={onTabSelected}
            />
          ))}
        </Div>
      </Div>

      {/* Tab Content */}
        {selectedSeasonId && (
          <>
            {selectedTab === "standings" && (
              <CompetitionStandingsTab
                key="standings"
                competitionId={competitionId}
                seasonId={selectedSeasonId}
                navigation={props.navigation}
              />
            )}
            {selectedTab === "topPlayers" && (
              <CompetitionTopPlayersTab
                key="topPlayers"
                competitionId={competitionId}
                seasonId={selectedSeasonId}
                season={seasons.find(s => s.id === selectedSeasonId) || null}
                navigation={props.navigation}
                teamSize={competition?.team_size}
              />
            )}
            {selectedTab === "playerStats" && (
              <CompetitionPlayerStatsTab
                key="playerStats"
                competitionId={competitionId}
                seasonId={selectedSeasonId}
                season={seasons.find(s => s.id === selectedSeasonId) || null}
                navigation={props.navigation}
              />
            )}
            {selectedTab === "matches" && (
              <CompetitionMatchesTab
                key="matches"
                competitionId={competitionId}
                seasonId={selectedSeasonId}
                navigation={props.navigation}
              />
            )}
          </>
        )}

      {/* No seasons message */}
      {seasons.length === 0 && (
        <Div classNames={["flex1", "alignItemsCenter", "justifyContentCenter", "mt-{30}"]}>
          <Label type="l1Bold" classNames={["textCenter", "colorSecondaryLight"]}>
            No seasons available
          </Label>
        </Div>
      )}
    </ScreenLayout>
  );
}
